import type { QueueItem } from "@/api/offlineQueue";
import { judgeCache } from "./cache";

/** A cache locator, never an authenticated session or permission grant. */
export interface OfflineJudgePointer {
  competitionId: string;
  principal: string;
  judgeNumber: number;
}
const key = "scorio.judge-offline-pointer.v1";
export function clearOfflineJudgePointer() {
  try {
    localStorage.removeItem(key);
  } catch {
    /* Storage unavailable. */
  }
}
export function saveOfflineJudgePointer(pointer: OfflineJudgePointer) {
  try {
    localStorage.setItem(key, JSON.stringify(pointer));
  } catch {
    /* Recovery optional. */
  }
}
export function offlineJudgePointer(): OfflineJudgePointer | null {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "null") as OfflineJudgePointer | null;
    return value &&
      typeof value.competitionId === "string" &&
      typeof value.principal === "string" &&
      value.principal.startsWith("link:") &&
      Number.isInteger(value.judgeNumber) &&
      value.judgeNumber >= 1 &&
      value.judgeNumber <= 16 &&
      judgeCache(value.principal, value.competitionId).read("warm") === true
      ? value
      : null;
  } catch {
    return null;
  }
}
function ownQueuedItems(pointer: OfflineJudgePointer, items: Record<string, QueueItem[]> = {}) {
  const own: Record<string, QueueItem[]> = {};
  for (const [scope, rows] of Object.entries(items)) {
    const [actor, cid, heat, seat] = JSON.parse(scope) as unknown[];
    if (
      actor === pointer.principal &&
      cid === pointer.competitionId &&
      seat === pointer.judgeNumber &&
      typeof heat === "string"
    )
      own[heat] = rows;
  }
  return own;
}
/** Read without constructing a queue, allocating identity, writing, or flushing. */
export async function readOfflineQueue(pointer: OfflineJudgePointer) {
  if (typeof indexedDB === "undefined") {
    const data = JSON.parse(localStorage.getItem("scorio.judge-queue.v1") ?? "null") as {
      items?: Record<string, QueueItem[]>;
    } | null;
    return ownQueuedItems(pointer, data?.items);
  }
  if (!(await indexedDB.databases()).some((db) => db.name === "scorio.judge-queue.v1")) return {};
  return new Promise<Record<string, QueueItem[]>>((resolve, reject) => {
    const open = indexedDB.open("scorio.judge-queue.v1");
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result;
      const tx = db.transaction("queue", "readonly");
      const request = tx.objectStore("queue").get("data");
      request.onsuccess = () => {
        try {
          resolve(ownQueuedItems(pointer, request.result?.items));
        } catch (error) {
          reject(error);
        }
      };
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => db.close();
    };
  });
}
